import { createBrowserRouter, RouterProvider, Navigate, Outlet, useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';
import { ThemeProvider } from './contexts/ThemeContext';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import LoginPage from './pages/LoginPage';
import NoticesPage from './pages/NoticesPage';
import NoticeDetailPage from './pages/NoticeDetailPage';
import CalendarPage from './pages/CalendarPage';
import KanbanPage from './pages/KanbanPage';
import KanbanCardPage from './pages/KanbanCardPage';
import MeetingNotePage from './pages/MeetingNotePage';
import MeetingNoteEditorPage from './pages/MeetingNoteEditorPage';
import ResearchLogPage from './pages/ResearchLogPage';
import ResearchLogEditorPage from './pages/ResearchLogEditorPage';
import PartnersPage from './pages/PartnersPage';
import AdminsPage from './pages/AdminsPage';
import AccessLogPage from './pages/AccessLogPage';
import SchedulePage from './pages/SchedulePage';
import GmailPage from './pages/GmailPage';
import DrivePage from './pages/DrivePage';
import GoogleConnectPage from './pages/GoogleConnectPage';
import EventsPage from './pages/EventsPage';
import EventEditorPage from './pages/EventEditorPage';
import EventSubPage from './pages/EventSubPage';
import RecruitPage from './pages/RecruitPage';
import QuotationPage from './pages/QuotationPage';
import ProposalPage from './pages/ProposalPage';
import CompetitorQuotePage from './pages/CompetitorQuotePage';

function SsoHandler() {
  const [params] = useSearchParams();
  const ssoToken = params.get('sso');

  useEffect(() => {
    if (!ssoToken) return;
    fetch('/api/auth/sso', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: ssoToken }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.token && data.admin) {
          localStorage.setItem('paper_work_token', data.token);
          localStorage.setItem('paper_work_user', JSON.stringify(data.admin));
          window.location.replace('/notices');
        } else {
          window.location.replace('/login');
        }
      })
      .catch(() => window.location.replace('/login'));
  }, [ssoToken]);

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <div style={{ color: '#aaa', fontSize: 14 }}>로그인 중...</div>
    </div>
  );
}

function RequireAuth() {
  const [params] = useSearchParams();
  const ssoToken = params.get('sso');
  if (ssoToken) return <SsoHandler />;
  const token = localStorage.getItem('paper_work_token');
  if (!token) return <Navigate to="/login" replace />;
  return (
    <Layout>
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
    </Layout>
  );
}

const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage onLogin={() => { window.location.href = '/notices'; }} />,
  },
  // 공유 링크 (비인증 접근 허용)
  { path: '/events/share/:shareId', element: <EventEditorPage /> },
  { path: '/events/view/:shareId', element: <EventEditorPage /> },
  {
    element: <RequireAuth />,
    children: [
      { path: '/', element: <Navigate to="/notices" replace /> },

      // 업무
      { path: '/notices', element: <NoticesPage /> },
      { path: '/notices/:id', element: <NoticeDetailPage /> },
      { path: '/calendar', element: <CalendarPage /> },
      { path: '/tasks', element: <KanbanPage /> },
      { path: '/tasks/:cardId', element: <KanbanCardPage /> },
      { path: '/work/meeting', element: <MeetingNotePage /> },
      { path: '/work/meeting/:id', element: <MeetingNoteEditorPage /> },
      { path: '/research', element: <ResearchLogPage /> },
      { path: '/research/:id', element: <ResearchLogEditorPage /> },

      // 행사
      { path: '/events', element: <EventsPage /> },
      { path: '/events/:id/edit', element: <EventEditorPage /> },
      { path: '/events/:id/:tab', element: <EventSubPage /> },
      { path: '/partners', element: <PartnersPage /> },
      { path: '/quotations',         element: <QuotationPage /> },
      { path: '/competitor-quotes', element: <CompetitorQuotePage /> },
      { path: '/proposals',         element: <ProposalPage /> },
      { path: '/recruit', element: <RecruitPage /> },

      // Google Chat
      { path: '/chat', element: null },
      { path: '/chat/:spaceId', element: null },

      // 메일 · 드라이브
      { path: '/gmail', element: <GmailPage /> },
      { path: '/drive', element: <DrivePage /> },
      { path: '/google/connect', element: <GoogleConnectPage /> },

      // 관리
      { path: '/schedule', element: <SchedulePage /> },
      { path: '/admins', element: <AdminsPage /> },
      { path: '/admins/access-logs', element: <AccessLogPage /> },
    ],
  },
  { path: '*', element: <Navigate to="/notices" replace /> },
]);

export default function App() {
  return (
    <ThemeProvider>
      <RouterProvider router={router} />
    </ThemeProvider>
  );
}
